import { useCallback, useEffect, useRef, useState } from 'react';
import Dimensions from "react-native-web/dist/exports/Dimensions";
import Platform from "react-native-web/dist/exports/Platform";
import debounce from 'lodash.debounce';
const DEBOUNCE_DELAY = 300;
const adjustHorizontalToFit = (initialOffset, windowWidth, sourceWidth) => {
  const offset = Math.max(0, initialOffset);
  const otherEdgeOverflow = Math.max(0, offset + sourceWidth - windowWidth);
  const tooWideBy = Math.max(0, otherEdgeOverflow - offset);
  const adjusted = {
    offset: Math.max(0, offset - otherEdgeOverflow)
  };
  if (tooWideBy) adjusted.width = Math.max(0, sourceWidth - tooWideBy);
  return adjusted;
};
const getPosition = _ref => {
  let {
    edge,
    fromEdge,
    sourceSize
  } = _ref;
  switch (edge) {
    case 'near':
      return fromEdge;
    case 'mid':
      return fromEdge + sourceSize / 2;
    case 'far':
      return fromEdge + sourceSize;
    default:
      return 0;
  }
};
const getEdgeType = (align, alignSide) => {
  const alignTo = align[alignSide];
  const edge = ['center', 'middle'].includes(alignTo) && 'mid' || (alignSide === alignTo ? 'near' : 'far');
  return edge;
};

/**
 * Based on UDS's private getTooltipPosition but generalised.
 *
 * Used for absolute positioning of the tooltip. Since the tooltip is always centered relatively
 * to the source (button) and we have a limited set of positions, an easy and consistent way
 * of positioning it is to check all of the possible positions and pick one that will be rendered
 * within the window bounds. This way we can also rely on the tooltip being actually rendered
 * before it is shown, which makes it account for the width being limiting in styles, custom font
 * rendering, etc.
 */
function getOverlaidPosition(_ref2) {
  let {
    sourceLayout,
    targetDimensions,
    windowDimensions,
    offsets = {},
    align
  } = _ref2;
  // Web-only: this will be difficult to mimic on native because there's no global scroll position.
  // TODO: wire something in e.g. a scroll ref accessible from a provider included in Allium provider
  // that can be passed to the appropriate ScrollView?
  const {
    scrollX = 0,
    scrollY = 0
  } = typeof window === 'object' ? window : {};

  // Will have top, bottom, left and/or right offsets depending on `align`
  const positioning = {};
  const verticalOffset = offsets.vertical ?? 0;
  const horizontalOffset = offsets.horizontal ?? 0;
  if (align.top) positioning.top = getPosition({
    edge: getEdgeType(align, 'top'),
    fromEdge: sourceLayout.y + scrollY + verticalOffset,
    sourceSize: sourceLayout.height
  });
  if (align.middle) positioning.top = getPosition({
    edge: getEdgeType(align, 'middle'),
    fromEdge: sourceLayout.y + scrollY + verticalOffset - targetDimensions.height / 2,
    sourceSize: sourceLayout.height
  });
  if (align.bottom) positioning.bottom = getPosition({
    edge: getEdgeType(align, 'bottom'),
    fromEdge: windowDimensions.height - (sourceLayout.y + scrollY + sourceLayout.height - verticalOffset),
    sourceSize: sourceLayout.height
  });
  if (align.left) positioning.left = getPosition({
    edge: getEdgeType(align, 'left'),
    fromEdge: sourceLayout.x + scrollX + horizontalOffset,
    sourceSize: sourceLayout.width
  });
  if (align.center) positioning.left = getPosition({
    edge: getEdgeType(align, 'center'),
    fromEdge: sourceLayout.x + scrollX + horizontalOffset - targetDimensions.width / 2,
    sourceSize: sourceLayout.width
  });
  if (align.right) positioning.right = getPosition({
    edge: getEdgeType(align, 'right'),
    fromEdge: windowDimensions.width - (sourceLayout.x + scrollX + sourceLayout.width - horizontalOffset),
    sourceSize: sourceLayout.width
  });
  if (!(align.left && align.right)) {
    // Check if the position and/or width need adjusting to fit on the screen
    const side = align.right ? 'right' : 'left';
    const adjusted = adjustHorizontalToFit(positioning[side], windowDimensions.width, sourceLayout.width);
    if (typeof adjusted.width === 'number') positioning.width = adjusted.width;
    if (typeof adjusted.offset === 'number') {
      positioning[side] = adjusted.offset;
    }
  }
  return positioning;
}

/**
 * Positions an element in a modal or portal so that it appears tooltip-like below the
 * target element.
 *
 * @TODO - add support for positioning other than 'below' like UDS's tooltip (this is not
 * a small task because UDS's tooltip logic only really works for short text - it might be
 * better to use a third-party library).
 */
const useOverlaidPosition = _ref3 => {
  let {
    isShown = false,
    offsets,
    // By default, align the overlaid target's `top` to the bottom of the source, and center horizontally.
    align = {
      center: 'center',
      top: 'bottom'
    }
  } = _ref3;
  // Element in main document flow that the targetRef element is positioned around
  const sourceRef = useRef(null);
  const [sourceLayout, setSourceLayout] = useState(null);

  // Element in a modal or portal overlay positioned to appear adjacent to sourceRef
  const targetRef = useRef(null);
  const [targetDimensions, setTargetDimensions] = useState(null);
  const [windowDimensions, setWindowDimensions] = useState(null);
  const onTargetLayout = useCallback(_ref4 => {
    let {
      nativeEvent: {
        layout: {
          width,
          height
        }
      }
    } = _ref4;
    // NOTE: UDS's Tooltip logic injects some additional width to allow for antialiasing etc of text,
    // avoiding adding unnecessary line breaks to text that is slightly wider than it thinks it is.
    // That is probably something specific to text tooltips that doesn't belong in a generic hook.
    setTargetDimensions(previousDimensions => {
      // Re-render on first non-zero width / height: avoid infinite loops on changes, or mispositioning
      // if user scrolls while a slidedown animation is changing the height and recalculating position.
      if (!previousDimensions && width && height) {
        return {
          width,
          height
        };
      }
      return previousDimensions;
    });
  }, []);
  const readyToShow = Boolean(isShown && sourceRef.current);
  useEffect(() => {
    const handleDimensionsChange = _ref5 => {
      let {
        window
      } = _ref5;
      const measurementFunction = Platform.OS === 'web' ? 'measureInWindow' : 'measure';
      sourceRef.current?.[measurementFunction]((x, y, width, height) => {
        setWindowDimensions(window);
        setSourceLayout({
          x,
          y,
          width,
          height
        });
      });
    };
    let subscription;
    const unsubscribe = () => {
      if (typeof subscription?.remove === 'function') {
        // React Native >=0.65.0
        subscription.remove();
      } else if (typeof Dimensions.remove === 'function') {
        // React Native <0.65.0
        Dimensions.remove('change', handleDimensionsChange);
      }
      setSourceLayout(null);
      setTargetDimensions(null);
    };
    if (readyToShow) {
      subscription = Dimensions.addEventListener('change', handleDimensionsChange);
      handleDimensionsChange({
        window: Dimensions.get('window')
      });
    } else {
      unsubscribe();
    }
    return unsubscribe;
  }, [readyToShow]);
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return undefined;
    }
    const handleScroll = debounce(() => {
      sourceRef.current?.measureInWindow((x, y, width, height) => {
        setWindowDimensions(window);
        setSourceLayout({
          x,
          y,
          width,
          height
        });
      });
    }, DEBOUNCE_DELAY);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [sourceRef]);
  const isReady = Boolean(isShown && sourceLayout && windowDimensions && targetDimensions);
  const overlaidPosition = isReady ? getOverlaidPosition({
    sourceLayout,
    targetDimensions,
    windowDimensions,
    offsets,
    align
  }) : {};
  return {
    overlaidPosition,
    sourceRef,
    targetRef,
    onTargetLayout,
    isReady
  };
};
export default useOverlaidPosition;